import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, BarChart3, RefreshCw, Banknote, Smartphone, CreditCard,
  Building, ReceiptText, Wallet, AlertTriangle, TrendingUp, ExternalLink,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";

const METHOD_META = {
  cash: { label: "Cash", color: "#2D6A4F", Icon: Banknote },
  mpesa: { label: "M-PESA", color: "#0F4C81", Icon: Smartphone },
  credit_card: { label: "Credit card", color: "#7B61FF", Icon: CreditCard },
  bank_transfer: { label: "Bank transfer", color: "#B87D07", Icon: Building },
  cheque: { label: "Cheque", color: "#525C58", Icon: ReceiptText },
};

const QUICK_RANGES = [
  { k: "this_month", label: "This month" },
  { k: "last_month", label: "Last month" },
  { k: "ytd", label: "Year to date" },
  { k: "last_12", label: "Last 12 months" },
];

const monthFirst = (d) => { const x = new Date(d); x.setDate(1); return x; };
const monthEnd = (d) => { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); return x; };
const fmt = (d) => d.toISOString().slice(0, 10);

function rangeFor(key) {
  const today = new Date();
  if (key === "this_month") return { from: fmt(monthFirst(today)), to: fmt(monthEnd(today)) };
  if (key === "last_month") {
    const lm = new Date(today.getFullYear(), today.getMonth() - 1, 15);
    return { from: fmt(monthFirst(lm)), to: fmt(monthEnd(lm)) };
  }
  if (key === "ytd") return { from: `${today.getFullYear()}-01-01`, to: fmt(today) };
  if (key === "last_12") {
    const start = new Date(today); start.setMonth(start.getMonth() - 11);
    return { from: fmt(monthFirst(start)), to: fmt(monthEnd(today)) };
  }
  return { from: fmt(monthFirst(today)), to: fmt(monthEnd(today)) };
}

export default function Finance() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState(() => rangeFor("this_month"));
  const [activeQuick, setActiveQuick] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/housing/finance?from_date=${range.from}&to_date=${range.to}`);
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const setQuick = (k) => { setActiveQuick(k); setRange(rangeFor(k)); };

  const methodBars = useMemo(() => {
    if (!data) return [];
    const kesEquiv = data.by_method.KES_equiv || data.by_method.KES || {};
    const entries = Object.entries(kesEquiv);
    const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
    return entries
      .map(([m, v]) => ({
        method: m,
        amount: v,
        pct: (v / total) * 100,
        count: data.by_method.counts?.[m] || 0,
        usd: (data.by_method.USD || {})[m] || 0,
        ...(METHOD_META[m] || { label: m, color: "#7A8A84", Icon: Wallet }),
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [data]);

  if (tab !== "overview") {
    return (
      <div className="space-y-6" data-testid="finance-page">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#7A8A84]" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <FinTabs tab={tab} setTab={setTab} />
        {tab === "accounts" && <AccountsTab />}
        {tab === "ledger" && <LedgerTab />}
        {tab === "clearance" && <ClearanceTab />}
      </div>
    );
  }

  if (loading || !data) return <div className="p-6 text-[#7A8A84]">Loading finance dashboard…</div>;

  const t = data.totals;
  const billed = t.billed_KES_equiv ?? t.billed_KES;
  const received = t.received_KES_equiv ?? t.received_KES;
  const outstanding = t.outstanding_KES_equiv ?? t.outstanding_KES;
  const collectionRate = billed > 0 ? (received / billed) * 100 : 0;
  const xr = data.exchange_rate || {};

  return (
    <div className="space-y-6" data-testid="finance-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#7A8A84]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <FinTabs tab={tab} setTab={setTab} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1E] flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-[#40916C]" /> Finance dashboard
          </h1>
          <p className="text-sm text-[#7A8A84]">Billed, received, outstanding — across visitor stays and long-term tenants.</p>
          {xr.usd_to_kes && (
            <p className="text-[11px] text-[#7A8A84] mt-1" data-testid="current-exchange-rate">
              All amounts rolled up in KES · USD → KES @ <span className="font-semibold text-[#1C1F1E]">{Number(xr.usd_to_kes).toFixed(2)}</span>
              {xr.is_default && <span className="ml-1 text-amber-700">(default — set your own in org settings)</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {QUICK_RANGES.map(({ k, label }) => (
            <button key={k} onClick={() => setQuick(k)}
              data-testid={`range-${k}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                activeQuick === k
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
              }`}>
              {label}
            </button>
          ))}
          <Input type="date" value={range.from} onChange={(e) => { setActiveQuick("custom"); setRange({ ...range, from: e.target.value }); }} className="w-36" data-testid="from-input" />
          <span className="text-[#7A8A84]">→</span>
          <Input type="date" value={range.to} onChange={(e) => { setActiveQuick("custom"); setRange({ ...range, to: e.target.value }); }} className="w-36" data-testid="to-input" />
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Billed" value={billed} sub={`${t.invoice_count} invoice${t.invoice_count === 1 ? "" : "s"}${t.billed_USD > 0 ? ` · incl. USD ${Number(t.billed_USD).toLocaleString()}` : ""}`} color="#1B4332" testId="stat-billed" />
        <StatCard label="Received" value={received} sub={`${t.payment_count} payment${t.payment_count === 1 ? "" : "s"}${t.received_USD > 0 ? ` · incl. USD ${Number(t.received_USD).toLocaleString()}` : ""}`} color="#2D6A4F" testId="stat-received" />
        <StatCard label="Outstanding" value={outstanding} sub={`across ${data.top_unpaid_invoices.length} open invoice${data.top_unpaid_invoices.length === 1 ? "" : "s"}${t.outstanding_USD > 0 ? ` · incl. USD ${Number(t.outstanding_USD).toLocaleString()}` : ""}`} color="#B5392F" testId="stat-outstanding" />
        <Card className="border-[#E8E8E6]" data-testid="stat-collection">
          <CardContent className="py-4">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Collection rate</p>
            <p className="text-2xl font-bold text-[#1B4332] mt-1">{collectionRate.toFixed(0)}%</p>
            <div className="h-2 mt-2 rounded-full bg-[#F4F4F1] overflow-hidden">
              <div className="h-full bg-[#40916C] rounded-full" style={{ width: `${Math.min(100, collectionRate)}%` }} />
            </div>
            <p className="text-[10px] text-[#7A8A84] mt-1">received / billed in window</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend + breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#E8E8E6] lg:col-span-2" data-testid="trend-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#40916C]" /> 12-month trend (KES)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#7A8A84" }} />
                <YAxis tick={{ fontSize: 10, fill: "#7A8A84" }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="billed_KES" fill="#1B4332" name="Billed" radius={[3, 3, 0, 0]} />
                <Bar dataKey="received_KES" fill="#40916C" name="Received" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-[#E8E8E6]" data-testid="method-breakdown-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#40916C]" /> Payment methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            {methodBars.length === 0 ? (
              <p className="text-sm text-[#7A8A84] py-8 text-center">No payments recorded in this window.</p>
            ) : (
              <div className="space-y-3">
                {methodBars.map(({ method, label, color, Icon, amount, pct, count, usd }) => (
                  <div key={method} data-testid={`method-${method}`} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 font-medium text-[#1C1F1E]">
                        <Icon className="h-3.5 w-3.5" style={{ color }} />
                        {label}
                        <span className="text-[10px] text-[#7A8A84] font-normal">({count})</span>
                      </span>
                      <span className="tabular-nums text-[#1C1F1E] font-medium">KES {amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F4F4F1] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <p className="text-[10px] text-[#7A8A84]">
                      {pct.toFixed(0)}% of payments
                      {usd > 0 && <> · incl. USD {Number(usd).toLocaleString()}</>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stale leases */}
      <Card className={`border ${data.stale_leases_count > 0 ? "border-amber-300" : "border-[#E8E8E6]"}`} data-testid="stale-leases-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${data.stale_leases_count > 0 ? "text-amber-600" : "text-[#7A8A84]"}`} />
            Tenants with outstanding balances ({data.stale_leases_count})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.stale_leases.length === 0 ? (
            <p className="text-sm text-[#7A8A84] py-6 text-center">All active tenants are paid up — beautiful work.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Billed</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Invoices</TableHead>
                <TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {data.stale_leases.map((l) => (
                  <TableRow key={l.lease_id} data-testid={`stale-lease-${l.lease_id}`}>
                    <TableCell className="font-medium">{l.tenant_name}</TableCell>
                    <TableCell className="text-xs text-[#525C58]">
                      {l.tenant_phone || "—"}<br />
                      {l.tenant_email || ""}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">KES {l.billed_KES.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">KES {l.paid_KES.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-red-600">KES {l.balance_KES.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{l.invoices_count}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/leases`)} className="text-[#1B4332]" data-testid={`open-lease-${l.lease_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Top unpaid in window */}
      <Card className="border-[#E8E8E6]" data-testid="top-unpaid-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-[#1B4332]" />
            Top open invoices in window ({data.top_unpaid_invoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.top_unpaid_invoices.length === 0 ? (
            <p className="text-sm text-[#7A8A84] py-6 text-center">Nothing open in this window.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Period / Issued</TableHead>
                <TableHead>Visitor / Tenant</TableHead>
                <TableHead className="text-right">Due (KES)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {data.top_unpaid_invoices.map((i) => (
                  <TableRow key={i.invoice_id} data-testid={`unpaid-${i.invoice_id}`}>
                    <TableCell className="font-mono">#{i.invoice_number}</TableCell>
                    <TableCell><Badge className={i.kind === "lease" ? "bg-purple-50 text-purple-800 border border-purple-200 text-[10px]" : "bg-sky-50 text-sky-800 border border-sky-200 text-[10px]"}>{i.kind}</Badge></TableCell>
                    <TableCell className="text-xs text-[#525C58]">{i.period || i.issued_at}</TableCell>
                    <TableCell>{i.visitor_name}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">KES {i.due_KES.toLocaleString()}</TableCell>
                    <TableCell><Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-xs">{i.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate("/invoices")} className="text-[#1B4332]" data-testid={`open-invoice-${i.invoice_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, color, testId }) {
  return (
    <Card className="border-[#E8E8E6]" data-testid={testId}>
      <CardContent className="py-4">
        <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">{label}</p>
        <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color }}>
          KES {Number(value || 0).toLocaleString()}
        </p>
        {sub && <p className="text-[10px] text-[#7A8A84] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function FinTabs({ tab, setTab }) {
  const tabs = [
    { k: "overview", label: "Overview" },
    { k: "accounts", label: "Chart of Accounts" },
    { k: "ledger", label: "General Ledger" },
    { k: "clearance", label: "Clearance Queue" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="finance-tabs">
      {tabs.map((t) => (
        <button key={t.k} onClick={() => setTab(t.k)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  tab === t.k ? "bg-[#1B4332] text-white border-[#1B4332]"
                              : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                }`}
                data-testid={`finance-tab-${t.k}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

const ACCOUNT_TYPES = ["income", "expense", "asset", "liability"];

function AccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", type: "income" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/accounts"); setAccounts(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load accounts"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/finance/accounts", form);
      toast.success(`Account ${form.code} added`);
      setForm({ code: "", name: "", type: "income" });
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const deactivate = async (a) => {
    if (!window.confirm(`Deactivate ${a.code} ${a.name}?`)) return;
    try { await api.delete(`/finance/accounts/${a.id}`); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  const typeColor = { income: "bg-emerald-100 text-emerald-800", expense: "bg-red-100 text-red-800",
                      asset: "bg-blue-100 text-blue-800", liability: "bg-amber-100 text-amber-900" };
  return (
    <Card className="border-[#E8E8E6]" data-testid="accounts-card">
      <CardHeader><CardTitle className="text-lg">Chart of Accounts</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={add} className="flex flex-wrap items-end gap-2 p-3 rounded-lg bg-[#F8F8F5] border border-[#E8E8E6]">
          <div><label className="text-xs text-[#525C58]">Code</label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="4004" className="w-24 h-9" required data-testid="account-code-input" /></div>
          <div className="flex-1 min-w-40"><label className="text-xs text-[#525C58]">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Conference Income" className="h-9" required data-testid="account-name-input" /></div>
          <div><label className="text-xs text-[#525C58]">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="h-9 border border-[#E8E8E6] rounded-md px-2 text-sm bg-white block" data-testid="account-type-select">
              {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <Button type="submit" disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F] h-9" data-testid="account-add-btn">Add account</Button>
        </form>
        <Table data-testid="accounts-table">
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {accounts.map((a) => (
              <TableRow key={a.id} className={a.active === false ? "opacity-40" : ""} data-testid={`account-row-${a.code}`}>
                <TableCell className="font-mono text-sm">{a.code}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell><Badge className={`${typeColor[a.type] || ""} border-0 text-[10px]`}>{a.type}</Badge></TableCell>
                <TableCell className="text-right">
                  {a.active !== false && (
                    <Button size="sm" variant="ghost" className="text-red-600 text-xs" onClick={() => deactivate(a)} data-testid={`account-deactivate-${a.code}`}>Deactivate</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function LedgerTab() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", account_code: "" });
  const [page, setPage] = useState(0);
  const PAGE = 100;
  const load = useCallback(async () => {
    const p = new URLSearchParams({ skip: String(page * PAGE), limit: String(PAGE) });
    if (filters.from_date) p.set("from_date", filters.from_date);
    if (filters.to_date) p.set("to_date", filters.to_date);
    if (filters.account_code) p.set("account_code", filters.account_code);
    try { const { data: d } = await api.get(`/finance/ledger?${p.toString()}`); setData(d); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load ledger"); }
  }, [filters, page]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const exportXlsx = async () => {
    const p = new URLSearchParams();
    if (filters.from_date) p.set("from_date", filters.from_date);
    if (filters.to_date) p.set("to_date", filters.to_date);
    if (filters.account_code) p.set("account_code", filters.account_code);
    try {
      const res = await api.get(`/finance/ledger/export?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "general_ledger.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  return (
    <Card className="border-[#E8E8E6]" data-testid="ledger-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between flex-wrap gap-3">
          <span>General Ledger {data && `(${data.total.toLocaleString()} entries)`}</span>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="ledger-export-btn">Export Excel</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={filters.from_date} onChange={(e) => { setPage(0); setFilters({ ...filters, from_date: e.target.value }); }} className="w-36 h-9" data-testid="ledger-from" />
          <span className="text-[#7A8A84]">→</span>
          <Input type="date" value={filters.to_date} onChange={(e) => { setPage(0); setFilters({ ...filters, to_date: e.target.value }); }} className="w-36 h-9" data-testid="ledger-to" />
          <Input value={filters.account_code} onChange={(e) => { setPage(0); setFilters({ ...filters, account_code: e.target.value }); }} placeholder="Account code…" className="w-36 h-9 font-mono" data-testid="ledger-account-filter" />
          {data && Object.entries(data.totals || {}).map(([cur, t]) => (
            <Badge key={cur} className="bg-[#E9F5DB] text-[#1B4332] border-0 text-[10px]" data-testid={`ledger-total-${cur}`}>
              {cur}: Dr {Number(t.debit).toLocaleString()} / Cr {Number(t.credit).toLocaleString()}
            </Badge>
          ))}
        </div>
        <Table data-testid="ledger-table">
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Account</TableHead><TableHead>Description</TableHead>
            <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
            <TableHead>Cur</TableHead><TableHead>Source</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((e) => (
              <TableRow key={e.ref_key} data-testid={`ledger-row-${e.ref_key}`}>
                <TableCell className="text-xs whitespace-nowrap">{e.date}</TableCell>
                <TableCell className="text-xs"><span className="font-mono">{e.account_code}</span> {e.account_name}</TableCell>
                <TableCell className="text-xs max-w-sm truncate" title={e.description}>{e.description}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{e.debit ? Number(e.debit).toLocaleString() : ""}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{e.credit ? Number(e.credit).toLocaleString() : ""}</TableCell>
                <TableCell className="text-xs">{e.currency}</TableCell>
                <TableCell><Badge className="bg-stone-100 text-stone-600 border-0 text-[9px]">{e.ref_type}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > PAGE && (
          <div className="flex items-center justify-between text-sm text-[#7A8A84]">
            <span>Page {page + 1} of {Math.ceil(data.total / PAGE)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClearanceTab() {
  const [rows, setRows] = useState(null);
  const [clearing, setClearing] = useState(null); // row
  const [note, setNote] = useState("");
  const navigate = useNavigate();
  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/clearance"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load clearance queue"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const doClear = async () => {
    try {
      await api.post(`/finance/clearance/${clearing.booking_id}/clear`, { note });
      toast.success(`${clearing.visitor_name} cleared`);
      setClearing(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="border-[#E8E8E6]" data-testid="clearance-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Needs clearing {rows && `(${rows.length})`}
        </CardTitle>
        <p className="text-xs text-[#7A8A84]">Guests who completed their stay but still have money owing or no payment record. They stay here until paid in full, proof is uploaded, or you clear them manually.</p>
      </CardHeader>
      <CardContent>
        {!rows ? <p className="text-sm text-[#7A8A84] py-4">Loading…</p>
        : rows.length === 0 ? (
          <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="clearance-empty">
            Everyone is cleared — no completed stays with outstanding balances.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.booking_id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50/40" data-testid={`clearance-row-${r.booking_id}`}>
                <div>
                  <p className="font-medium text-[#1C1F1E]">{r.visitor_name} {r.visitor_no && <span className="text-xs text-[#7A8A84]">({r.visitor_no})</span>}</p>
                  <p className="text-xs text-[#7A8A84]">{r.stay_from} → {r.stay_to} · {r.reason}
                    {r.has_payment_proof && <span className="text-emerald-700"> · proof uploaded</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {r.outstanding > 0 && <Badge className="bg-red-100 text-red-800 border-0 tabular-nums">KES-eq {Number(r.outstanding).toLocaleString()}</Badge>}
                  <Button size="sm" variant="outline" onClick={() => navigate("/invoices")} data-testid={`clearance-invoices-${r.booking_id}`}>
                    {r.has_invoice ? "Record payment" : "Raise invoice"}
                  </Button>
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800"
                          onClick={() => { setClearing(r); setNote(""); }} data-testid={`clearance-clear-${r.booking_id}`}>
                    Mark cleared
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {clearing && (
          <div className="mt-4 p-3 rounded-lg border border-[#E8E8E6] bg-[#F8F8F5] space-y-2" data-testid="clearance-confirm">
            <p className="text-sm">Clear <b>{clearing.visitor_name}</b> manually? Add a note (e.g. "waived", "paid via agency"):</p>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note" className="h-9" data-testid="clearance-note" />
            <div className="flex gap-2">
              <Button size="sm" className="bg-[#1B4332] hover:bg-[#2D6A4F]" onClick={doClear} data-testid="clearance-confirm-btn">Confirm clear</Button>
              <Button size="sm" variant="outline" onClick={() => setClearing(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
